import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';

export interface RateLimitTotals {
  throttled: number;
  /** Calls our limiter held back — the cost of not being throttled. */
  deferred: number;
  /** The other six log_custom_event reasons, kept apart so deferrals stay readable. */
  other_refusals: number;
}

export interface SubscriberPeak {
  subscriber_id: number;
  name: string | null;
  peak_per_second: number;
}

export interface SubscriberThrottled {
  subscriber_id: number;
  name: string | null;
  throttled: number;
}

// count/min/max only — a median would have to sort every reading in the window.
export interface RemainingBudget {
  observed: number;
  min?: number;
  max?: number;
}

export interface SyncTypeCounts {
  calls: number;
  throttled: number;
}

export interface ThrottledBucket {
  at: string;
  count: number;
  vendor: string;
}

export interface SchoologyGlobal {
  calls: number;
  peak_per_second: number;
  seconds_over_ceiling: number;
  ceiling: number;
}

export interface RateLimitPayload {
  window: string;
  since: string;
  totals: RateLimitTotals;
  throttled_by_vendor: Record<string, number>;
  /** Grade sync and roster sync share a vendor budget but are driven separately — the
   *  assignee-refresh throttle moves roster sync only. */
  calls_by_sync_type: { grade_sync: SyncTypeCounts; roster_sync: SyncTypeCounts };
  throttled_over_time: ThrottledBucket[];
  peak_per_second: SubscriberPeak[];
  /** Always 24h — the per-second group-by is too expensive over a longer window. */
  peak_per_second_window: string;
  /** Schoology meters every district as one client, so only the aggregate reads. */
  schoology_global: SchoologyGlobal;
  remaining_budget: RemainingBudget;
  subscribers: SubscriberThrottled[];
}

const useRateLimits = (window: string) => useQuery({
  queryFn: async (): Promise<RateLimitPayload> => {
    const { data } = await axios.get(Routes.plato_api_sysadmin_grade_sync_rate_limits_path(), {
      params: { window },
    });

    return data.data;
  },
  queryKey: ['gradeSyncRateLimits', window],
});

export default useRateLimits;
